// src/utils/openingHoursUtils.js
// ─────────────────────────────────────────────────────────
// 🔧 Utilitaire centralisé pour les horaires d'ouverture
// Standard : clés EN (monday, tuesday...) en BDD
// Affichage : noms FR (Lundi, Mardi...)
// ─────────────────────────────────────────────────────────

// Mapping FR → EN (pour normaliser les anciennes données)
export const FR_TO_EN = {
  lundi: 'monday',
  mardi: 'tuesday',
  mercredi: 'wednesday',
  jeudi: 'thursday',
  vendredi: 'friday',
  samedi: 'saturday',
  dimanche: 'sunday'
};

// Mapping EN → FR (pour l'affichage)
export const EN_TO_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

// Ordre canonique des jours
export const DAY_ORDER = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

// Horaires par défaut (clés EN)
export const DEFAULT_HOURS = {
  monday:    { open: '09:00', close: '18:00', closed: false },
  tuesday:   { open: '09:00', close: '18:00', closed: false },
  wednesday: { open: '09:00', close: '18:00', closed: false },
  thursday:  { open: '09:00', close: '18:00', closed: false },
  friday:    { open: '09:00', close: '18:00', closed: false },
  saturday:  { open: '09:00', close: '18:00', closed: false },
  sunday:    { open: '09:00', close: '18:00', closed: true  }
};

/**
 * Normalise les horaires : convertit les clés FR en EN si nécessaire.
 * À appeler à chaque lecture depuis la BDD (lecture côté salon et côté client).
 *
 * @param {Object|string|null} hours - Horaires bruts (peut être string JSON)
 * @returns {Object} Horaires normalisés avec clés EN et tous les jours présents
 */
export const normalizeOpeningHours = (hours) => {
  if (!hours) return { ...DEFAULT_HOURS };

  // Parser si c'est une string JSON
  let parsed = hours;
  if (typeof hours === 'string') {
    try {
      parsed = JSON.parse(hours);
    } catch (e) {
      console.error('Erreur parsing opening_hours:', e);
      return { ...DEFAULT_HOURS };
    }
  }

  const normalized = {};

  Object.entries(parsed).forEach(([key, value]) => {
    // Convertir la clé FR → EN si nécessaire
    const englishKey = FR_TO_EN[key] || key;

    // Vérifier que c'est un jour valide
    if (DAY_ORDER.includes(englishKey)) {
      normalized[englishKey] = {
        open:   value.open   || '09:00',
        close:  value.close  || '18:00',
        closed: value.closed ?? false
      };
    }
  });

  // Ajouter les jours manquants avec les valeurs par défaut
  DAY_ORDER.forEach(day => {
    if (!normalized[day]) {
      normalized[day] = { ...DEFAULT_HOURS[day] };
    }
  });

  return normalized;
};

/**
 * Trier les horaires dans l'ordre canonique de la semaine.
 * @param {Object} hours - Horaires normalisés (clés EN)
 * @returns {Array} Tableau ordonné [{ day: 'monday', label: 'Lundi', hours: {...} }, ...]
 */
export const getSortedHours = (hours) => {
  const normalized = normalizeOpeningHours(hours);

  return DAY_ORDER.map(day => ({
    day,
    label: EN_TO_FR[day],
    hours: normalized[day]
  }));
};

/**
 * Obtenir le nom français d'un jour anglais.
 * @param {string} day - Clé anglaise (monday, tuesday...)
 * @returns {string} Nom français avec majuscule (Lundi, Mardi...)
 */
export const getDayLabel = (day) => {
  return EN_TO_FR[day] || (day.charAt(0).toUpperCase() + day.slice(1));
};